import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import { Fade, InputAdornment, MenuItem, Select, TextField, Button } from '@mui/material';
import Footer from 'components/common/Footer/Footer';
import HeaderLogo from 'components/common/HeaderLogo/HeaderLogo';
import ExternalClientAccessInfo from 'components/common/ExternalClientAccessInfo/ExternalClientAccessInfo';
import NumberFormatCustom from 'components/common/NumberFormatCustom';
import HeadcountSlider from 'components/common/HeadcountSlider';
import WarningMessage from 'components/common/WarningMessage/WarningMessage';
import InfoMessage from 'components/common/InfoMessage/InfoMessage';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useValidator } from 'hooks/useValidator';
import actions from 'store/actions';
import { updateNA, updateExternalAccessNA } from 'store/na/thunk';
import information from '../../img/information.svg';
import styles from './step2.module.scss';
import { HtmlTooltip } from 'constants/tooltip';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PercentIcon } from 'icons/percent.svg';
import { ReactComponent as SliderIcon } from 'icons/slider.svg';
import SharedSeatsRatios from './SharedSeatsRatios';
import searchParams from 'constants/searchParams';
import paths from 'constants/paths';
import { useStepsWizard } from 'hooks/useStepsWizard';

const minDistance = 25;
const sliderMax = 100 + minDistance * 3;

export default function Step2() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = new URLSearchParams(window.location.search);
  const preview = params.get(searchParams.preview);
  const na = useSelector((state) => state.na);
  const isExternalAccess = useSelector((state) => state.general.isExternalAccessMode);
  const isReadOnlyMode = useSelector((state) => state.general.isReadOnlyMode);
  const externalAccessConfig = useSelector((state) => state.externalAccessConfig);
  const [validationModalShown, setValidationModalShown] = useState(false);
  const [headcount, setHeadcount] = useState(na.headcount);
  const [inOffice, setInOffice] = useState(na.inOffice);
  const [fullyRemote, setFullyRemote] = useState(na.fullyRemote);
  const [hybrid, setHybrid] = useState(na.hybrid);
  const [daysInOffice, setDaysInOffice] = useState(na.daysInOffice);
  const [daysInOfficeEnabled, setDaysInOfficeEnabled] = useState(na.hybrid && na.hybrid > 0);
  const [sliderValue, setSliderValue] = useState([
    headcount > 0 ? na.inOffice + minDistance : 33 + minDistance,
    headcount > 0 ? na.inOffice + na.fullyRemote + minDistance * 2 : 66 + minDistance * 2,
  ]);
  const [showSlider, setShowSlider] = useState(true);
  const { goToStep } = useStepsWizard({
    naId: na.id,
    preview,
    isExternalAccess,
    accessKey: externalAccessConfig.accessKey,
  });
  const fullyInOfficeTooltip = (
    <div className={styles.tooltip}>
      <div className={styles.title}>{t('step2.inOffice')}</div>
      <div className={styles.description}>{t('step2.inOfficeTooltip')}</div>
    </div>
  );
  const fullyRemoteTooltip = (
    <div className={styles.tooltip}>
      <div className={styles.title}> {t('step2.remote')}</div>
      <div className={styles.description}> {t('step2.remoteTooltip')}</div>
    </div>
  );
  const hybridTooltip = (
    <div className={styles.tooltip}>
      <div className={styles.title}> {t('step2.hybrid')}</div>
      <div className={styles.description}>{t('step2.hybridTooltip')}</div>
    </div>
  );

  useEffect(() => {
    const daysInOfficeState = hybrid > 0;
    setDaysInOfficeEnabled(daysInOfficeState);
  }, [hybrid]);

  const setSliderValues = useCallback(() => {
    setInOffice(sliderValue[0] - minDistance);
    setFullyRemote(sliderValue[1] - sliderValue[0] - minDistance);
    setHybrid(sliderMax - sliderValue[1] - minDistance);
  }, [sliderValue]);

  useEffect(() => {
    if (headcount > 0) setSliderValues();
  }, [headcount, setSliderValues]);

  const handleSliderChange = (event, newValue, activeThumb) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    if (newValue[0] < minDistance && activeThumb === 0) {
      setSliderValue([minDistance, newValue[1] >= minDistance * 2 ? newValue[1] : minDistance * 2]);
    } else if (newValue[1] > sliderMax - minDistance && newValue[0] < sliderMax - minDistance * 2) {
      setSliderValue([newValue[0], sliderMax - minDistance]);
    }

    if (newValue[1] - newValue[0] < minDistance) {
      if (activeThumb === 0 && newValue[0] > minDistance) {
        if (newValue[1] < sliderMax - minDistance) {
          const clamped = Math.min(newValue[0], sliderMax - minDistance);
          if (clamped <= sliderMax - minDistance * 2) {
            setSliderValue([clamped, clamped + minDistance]);
          }
        }
      } else {
        if (newValue[0] > minDistance) {
          const clamped = Math.max(newValue[1], minDistance);
          if (clamped >= minDistance * 2 && sliderMax - newValue[1] > minDistance) {
            setSliderValue([clamped - minDistance, clamped]);
          }
        }
      }
    } else {
      if (newValue[0] >= minDistance && newValue[1] <= sliderMax - minDistance) {
        setSliderValue(newValue);
      }
    }
    setSliderValues();
  };

  const handleEmpty = (value, setHandler, setEmpty) => {
    value = value.replace(/\D/g, '');
    if (setEmpty) {
      setHandler(value === '0' ? '' : Number(value));
    } else {
      setHandler(value === '' ? 0 : Number(value));
    }
  };

  const handleChange = (value, setHandler) => {
    setHandler(value === '' ? value : Number(value));
  };

  const toggleSlider = () => {
    if (!validator.validate()) return;

    setSliderValue([minDistance + inOffice, minDistance * 2 + inOffice + fullyRemote]);
    setShowSlider(!showSlider);
  };

  const closeValidationModal = () => {
    setValidationModalShown(false);
  };

  const showValidationModal = () => {
    setValidationModalShown(true);
  };

  const [validables, validator] = useValidator(['headcount', 'sumpercentages', 'errors'], () => {
    const message = {};

    if (headcount < 1) message.headcount = t('step2.headcountError');
    if (inOffice + fullyRemote + hybrid !== 100) message.sumpercentages = t('step2.inputsError');
    const anyError = message.headcount || message.sumpercentages;
    if (!!anyError) message.errors = anyError;

    return message;
  });

  const updateDataStore = (step) => {
    if (!validator.validate()) {
      if (isReadOnlyMode) showValidationModal();

      return false;
    }

    const dirty =
      headcount !== na.headcount ||
      inOffice !== na.inOffice ||
      fullyRemote !== na.fullyRemote ||
      hybrid !== na.hybrid ||
      daysInOffice !== na.daysInOffice;
    if (dirty) {
      dispatch(actions.na.updateWorkplaceStyleSelected(2));
      dispatch(actions.na.updateParams(headcount, inOffice, fullyRemote, hybrid, daysInOffice));
    }

    if (!isReadOnlyMode) {
      dispatch(actions.na.updateLastStep(step));
      dispatch(isExternalAccess ? updateExternalAccessNA({ hasChanged: dirty }) : updateNA);
    }

    return true;
  };

  const handleNext = () => {
    if (updateDataStore(paths.steps.yourGrowth)) goToStep({ step: paths.steps.yourGrowth });
  };

  const handleBack = () => {
    if (updateDataStore(paths.steps.currentSituation))
      goToStep({ step: paths.steps.currentSituation });
  };

  return (
    <>
      {isReadOnlyMode && !isExternalAccess && <ExternalClientAccessInfo />}
      <div className={styles.full}>
        <WarningMessage />
        <InfoMessage
          show={validationModalShown}
          onClose={closeValidationModal}
          message={t('externalAccess.step2validationMessage')}
        />
        <div className={styles.column1}>
          <HeaderLogo />
          <div className={styles.row2}>
            <div className={styles.title}>{t('step2.title')}</div>
            <div className={styles.description}>{t('step2.description')}</div>
            <div className={styles.field_title}>{t('step2.headcount')}</div>
            <TextField
              disabled={isReadOnlyMode}
              name="headcount_field"
              className={styles.input}
              value={headcount}
              onFocus={(event) => handleEmpty(event.target.value, setHeadcount, true)}
              onBlur={(event) => handleEmpty(event.target.value, setHeadcount, false)}
              onChange={(event) => handleChange(event.target.value, setHeadcount)}
              sx={{ input: { textAlign: 'center' } }}
              inputProps={{ 'data-testid': 'Step2HeadCountText', min: 0, max: 1000000 }}
              InputProps={{
                inputComponent: NumberFormatCustom,
                endAdornment: (
                  <InputAdornment position="start">
                    <PeopleAltOutlinedIcon />
                  </InputAdornment>
                ),
              }}
              error={!validables.headcount.valid}
            />
            <div className={styles.calculation__header}>
              <h3 className={styles.calculation__title}>{t('step2.percentagesTitle')}</h3>
              <Button
                disabled={isReadOnlyMode}
                data-testid="ToggleSliderAndInputs"
                className={styles.calculation__toggle_slider_btn}
                size="small"
                variant="contained"
                startIcon={showSlider ? <PercentIcon /> : <SliderIcon />}
                onClick={toggleSlider}>
                {showSlider ? t('step2.showInputs') : t('step2.showSlider')}
              </Button>
            </div>
            {showSlider ? (
              <HeadcountSlider
                disabled={isReadOnlyMode}
                value={sliderValue}
                onChange={handleSliderChange}
                headcount={headcount}
                inOffice={inOffice}
                fullyRemote={fullyRemote}
                hybrid={hybrid}
                max={sliderMax}
                initialScreen={na.headcount}
                hybridTooltip={hybridTooltip}
                fullyRemoteTooltip={fullyRemoteTooltip}
                fullyInOfficeTooltip={fullyInOfficeTooltip}
              />
            ) : (
              <div
                data-testid="HeadcountInputs"
                className={`${styles.rowfields} ${styles.inputs__container}`}>
                <div className={styles.field_container}>
                  <div className={styles.field_title}>
                    <div>{t('step2.inOffice')}</div>
                    <HtmlTooltip
                      followCursor
                      title={fullyInOfficeTooltip}
                      TransitionComponent={Fade}
                      placement="right">
                      <img src={information} alt="" />
                    </HtmlTooltip>
                  </div>
                  <TextField
                    name="in_office_field"
                    className={styles.input}
                    value={inOffice}
                    onFocus={(event) => handleEmpty(event.target.value, setInOffice, true)}
                    onBlur={(event) => handleEmpty(event.target.value, setInOffice, false)}
                    onChange={(event) => handleChange(event.target.value, setInOffice)}
                    sx={{ input: { textAlign: 'center' } }}
                    inputProps={{
                      min: 0,
                      max: 100,
                      'data-testid': 'Step2InOfficeInput',
                    }}
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                      endAdornment: (
                        <InputAdornment position="start">
                          <PercentOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                    error={!validables.sumpercentages.valid}
                  />
                </div>
                <div className={styles.field_container}>
                  <div className={styles.field_title}>
                    <div>{t('step2.remote')}</div>
                    <HtmlTooltip
                      followCursor
                      title={fullyRemoteTooltip}
                      TransitionComponent={Fade}
                      placement="right">
                      <img src={information} alt="" />
                    </HtmlTooltip>
                  </div>
                  <TextField
                    name="fully_remote_field"
                    className={styles.input}
                    value={fullyRemote}
                    onFocus={(event) => handleEmpty(event.target.value, setFullyRemote, true)}
                    onBlur={(event) => handleEmpty(event.target.value, setFullyRemote, false)}
                    onChange={(event) => handleChange(event.target.value, setFullyRemote)}
                    sx={{ input: { textAlign: 'center' } }}
                    inputProps={{ min: 0, max: 100, 'data-testid': 'Step2RemoteInput' }}
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                      endAdornment: (
                        <InputAdornment position="start">
                          <PercentOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                    error={!validables.sumpercentages.valid}
                  />
                </div>
                <div className={styles.fullyremote}></div>
                <div className={styles.field_container}>
                  <div className={styles.field_title}>
                    <div>{t('step2.hybrid')}</div>
                    <HtmlTooltip
                      followCursor
                      title={hybridTooltip}
                      TransitionComponent={Fade}
                      placement="right">
                      <img src={information} alt="" />
                    </HtmlTooltip>
                  </div>
                  <TextField
                    name="hybrid_field"
                    className={styles.input}
                    value={hybrid}
                    onFocus={(event) => handleEmpty(event.target.value, setHybrid, true)}
                    onBlur={(event) => handleEmpty(event.target.value, setHybrid, false)}
                    onChange={(event) => {
                      const daysInOfficeState = event.target.value && event.target.value > 0;
                      setDaysInOfficeEnabled(daysInOfficeState);
                      handleChange(event.target.value, setHybrid);
                    }}
                    sx={{ input: { textAlign: 'center' } }}
                    inputProps={{ min: 0, max: 100, 'data-testid': 'Step2HybridInput' }}
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                      endAdornment: (
                        <InputAdornment position="start">
                          <PercentOutlinedIcon />
                        </InputAdornment>
                      ),
                    }}
                    error={!validables.sumpercentages.valid}
                  />
                </div>
              </div>
            )}
            <div className={styles.rowfields}>
              <div>
                <div className={styles.field_title}>
                  <div>{t('step2.daysInOffice')}</div>
                  <SharedSeatsRatios
                    isExternalAccess={isExternalAccess}
                    disabled={isReadOnlyMode}
                  />
                </div>
                <Select
                  data-testid={'Step2Selector'}
                  disabled={!daysInOfficeEnabled || isReadOnlyMode}
                  className={`${styles.input} ${isReadOnlyMode && styles.disabled}`}
                  value={daysInOfficeEnabled ? daysInOffice : 0}
                  onChange={(option) => setDaysInOffice(option.target.value)}>
                  <MenuItem sx={{ display: 'none' }} value={0}>
                    0
                  </MenuItem>
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={4}>4</MenuItem>
                  <MenuItem value={5}>5</MenuItem>
                </Select>
              </div>
            </div>
            <div className={styles.errorline}>{validables.errors.message}</div>
          </div>
          <div className={styles.row3}>
            <Footer
              title={t('step2.footerTitle')}
              description={t('step2.footerDescription')}
              stepsHighlighted="2"
              handleback={handleBack}
              handlenext={handleNext}></Footer>
          </div>
        </div>
        <div className={styles.column2}></div>
      </div>
    </>
  );
}
